import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { IThunkRejectValue, RootState } from "../../../types";
import { AiJob, AiJobsPresetImage, AiJobsState } from "../../../types/ai";
import { getExtractErrors } from "../../../apis";
import { CustomErrorToast } from "../../../components/general/Toast";
import {
  getAiJobsApi,
  createAiJobApi,
  deleteAiJobApi,
  updateAiJobApi,
  stopAiJobApi,
  getAiJobPresetImageApi,
} from "../../../apis/ai-jobsAPI";

export const AiJobsRefreshStatus = [
  "DELETING",
  "RESTARTING",
  //"RUNNING"
  "STARTING",
  "STOPPING",
  "FINALIZING",
];

const initialState: AiJobsState = {
  jobs: [],
  jobsLoading: false,
  jobsActionLoading: false, //for create, delete, rename , update
  selectedJob: null, //for rename
  presetImages: [],
  presetImagesLoading: false,
};

export const getAiJobsAsync = createAsyncThunk<
  { jobs: Array<AiJob> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-jobs",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiJobsAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiJobsApi();
      const jobs = response.data.Result;
      return fulfillWithValue({ jobs });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

export const updateAiJobAsync = createAsyncThunk<
  any,
  { id: string; data: any },
  IThunkRejectValue
>("ai-jobs/update", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateAiJobApi(id, data);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const createAiJobAsync = createAsyncThunk<
  true,
  { data: any },
  IThunkRejectValue
>("ai-jobs/create", async ({ data }, { rejectWithValue }) => {
  try {
    await createAiJobApi(data);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const deleteAiJobAsync = createAsyncThunk<
  true,
  { jobId: string },
  IThunkRejectValue
>("ai-jobs/delete", async ({ jobId }, { rejectWithValue }) => {
  try {
    await deleteAiJobApi(jobId);
    return true;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const stopAiJobAsync = createAsyncThunk<
  any,
  { id: string },
  IThunkRejectValue
>("ai-jobs/stop", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await stopAiJobApi(id);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const relaunchAiJobAsync = createAsyncThunk<
  any,
  { job: AiJob },
  IThunkRejectValue
>("ai-jobs/relaunch", async ({ job }, { rejectWithValue }) => {
  try {
    const response = await createAiJobApi(job.spec);
    return response;
  } catch (e) {
    return rejectWithValue({ message: getExtractErrors(e) });
  }
});

export const getAiJobsPresetImageAsync = createAsyncThunk<
  { presetImages: Array<AiJobsPresetImage> },
  { withoutLoading?: boolean },
  IThunkRejectValue
>(
  "ai-jobs/capabilities/presetImages",
  async (
    { withoutLoading = false },
    { rejectWithValue, fulfillWithValue, dispatch, requestId }
  ) => {
    try {
      dispatch(
        getAiJobsPresetImageAsync.pending(requestId, {
          withoutLoading,
        })
      );

      const response = await getAiJobPresetImageApi();
      const presetImages = response.data.Result;
      return fulfillWithValue({ presetImages });
    } catch (e) {
      return rejectWithValue({ message: getExtractErrors(e) });
    }
  }
);

// export const getAiJobsFreamworksAsync = createAsyncThunk<
//   { freamworks: Array<AiJobsFreamwork> },
//   { withoutLoading?: boolean },
//   IThunkRejectValue
// >(
//   "ai-jobs/freamworks",
//   async (
//     { withoutLoading = false },
//     { rejectWithValue, fulfillWithValue, dispatch, requestId }
//   ) => {
//     try {
//       dispatch(
//         getAiJobsFreamworksAsync.pending(requestId, {
//           withoutLoading,
//         })
//       );

//       const response = await getAiJobsFreamworkApi();
//       const freamworks = response.data.Result;
//       return fulfillWithValue({ freamworks });
//     } catch (e) {
//       return rejectWithValue({ message: getExtractErrors(e) });
//     }
//   }
// );

// export const getAiRegionsDataAsync = createAsyncThunk<
//   { regionsData: Array<AiRegionData> },
//   { withoutLoading?: boolean },
//   IThunkRejectValue
// >(
//   "ai-lab/regions-data",
//   async (
//     { withoutLoading = false },
//     { rejectWithValue, fulfillWithValue, dispatch, requestId }
//   ) => {
//     try {
//       dispatch(
//         getAiRegionsDataAsync.pending(requestId, {
//           withoutLoading,
//         })
//       );

//       const response = await getAiRegionsDataApi();
//       const regionsData = response.data.Result;
//       return fulfillWithValue({ regionsData });
//     } catch (e) {
//       return rejectWithValue({ message: getExtractErrors(e) });
//     }
//   }
// );

// export const getAiResourceFlavorsAsync = createAsyncThunk<
//   { flavors: Array<AiResourceFlavor> },
//   { region: string; withoutLoading?: boolean },
//   IThunkRejectValue
// >(
//   "ai-lab/resource-flavors",
//   async (
//     { region, withoutLoading = false },
//     { rejectWithValue, fulfillWithValue, dispatch, requestId }
//   ) => {
//     try {
//       dispatch(
//         getAiResourceFlavorsAsync.pending(requestId, {
//           withoutLoading,
//           region,
//         })
//       );

//       const response = await getAiResourceFlavorsApi(region);
//       const flavors = response.data.Result;
//       return fulfillWithValue({ flavors });
//     } catch (e) {
//       return rejectWithValue({ message: getExtractErrors(e) });
//     }
//   }
// );

export const aiJobsSlice = createSlice({
  name: "ai-jobs",
  initialState,
  reducers: {
    handleSelectJob: (state, action: PayloadAction<AiJob>) => {
      state.selectedJob = action.payload;
    },
    // handleToggleAiJobsLabelModal: (
    //   state,
    //   action: PayloadAction<{ job: AiJobs | null; isOpen: boolean }>
    // ) => {
    //   state.selectedJob = action.payload.job;
    //   state.labelEditor = action.payload.isOpen;
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAiJobsAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.jobsLoading = true;
      })

      .addCase(getAiJobsAsync.fulfilled, (state, action) => {
        const { jobs } = action.payload;
        state.jobs = jobs;
        state.jobsLoading = false;
      })
      .addCase(getAiJobsAsync.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(updateAiJobAsync.pending, (state) => {
        state.jobsActionLoading = true;
      })
      .addCase(updateAiJobAsync.fulfilled, (state) => {
        state.jobsActionLoading = false;
      })
      .addCase(updateAiJobAsync.rejected, (state, { payload }) => {
        state.jobsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(createAiJobAsync.pending, (state) => {
        state.jobsActionLoading = true;
      })
      .addCase(createAiJobAsync.fulfilled, (state) => {
        state.jobsActionLoading = false;
      })
      .addCase(createAiJobAsync.rejected, (state, { payload }) => {
        state.jobsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(deleteAiJobAsync.pending, (state) => {
        state.jobsActionLoading = true;
      })
      .addCase(deleteAiJobAsync.fulfilled, (state) => {
        state.jobsActionLoading = false;
      })
      .addCase(deleteAiJobAsync.rejected, (state, { payload }) => {
        state.jobsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(stopAiJobAsync.pending, (state) => {
        state.jobsActionLoading = true;
      })
      .addCase(stopAiJobAsync.fulfilled, (state) => {
        state.jobsActionLoading = false;
      })
      .addCase(stopAiJobAsync.rejected, (state, { payload }) => {
        state.jobsActionLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(relaunchAiJobAsync.pending, (state) => {
        state.jobsActionLoading = true;
      })
      .addCase(relaunchAiJobAsync.fulfilled, (state) => {
        state.jobsActionLoading = false;
      })
      .addCase(relaunchAiJobAsync.rejected, (state, { payload }) => {
        state.presetImagesLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    builder
      .addCase(getAiJobsPresetImageAsync.pending, (state, action) => {
        if (!action.meta.arg.withoutLoading) state.presetImagesLoading = true;
      })
      .addCase(getAiJobsPresetImageAsync.fulfilled, (state, action) => {
        const { presetImages } = action.payload;
        state.presetImages = presetImages;
        state.presetImagesLoading = false;
      })
      .addCase(getAiJobsPresetImageAsync.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        if (payload?.message)
          toast.error(() => CustomErrorToast(payload?.message));
      });

    //editors
    // builder
    //   .addCase(getAiJobsEditorsAsync.pending, (state, action) => {
    //     if (!action.meta.arg.withoutLoading) state.editorsLoading = true;
    //   })
    //   .addCase(getAiJobsEditorsAsync.fulfilled, (state, action) => {
    //     const { editors } = action.payload;
    //     state.editors = editors;
    //     state.editorsLoading = false;
    //   })
    //   .addCase(getAiJobsEditorsAsync.rejected, (state, { payload }) => {
    //     state.editorsLoading = false;
    //     if (payload?.message)
    //       toast.error(() => CustomErrorToast(payload?.message));
    //   });

    //freamworks
    // builder
    //   .addCase(getAiJobsFreamworksAsync.pending, (state, action) => {
    //     if (!action.meta.arg.withoutLoading) state.freamworksLoading = true;
    //   })
    //   .addCase(getAiJobsFreamworksAsync.fulfilled, (state, action) => {
    //     const { freamworks } = action.payload;
    //     state.freamworks = freamworks;
    //     state.freamworksLoading = false;
    //   })
    //   .addCase(getAiJobsFreamworksAsync.rejected, (state, { payload }) => {
    //     state.freamworksLoading = false;
    //     if (payload?.message)
    //       toast.error(() => CustomErrorToast(payload?.message));
    //   });

    //regions data
    // builder
    //   .addCase(getAiRegionsDataAsync.pending, (state, action) => {
    //     if (!action.meta.arg.withoutLoading) state.regionsDataLoading = true;
    //   })
    //   .addCase(getAiRegionsDataAsync.fulfilled, (state, action) => {
    //     const { regionsData } = action.payload;
    //     state.regionsData = regionsData;
    //     state.regionsDataLoading = false;
    //   })
    //   .addCase(getAiRegionsDataAsync.rejected, (state, { payload }) => {
    //     state.regionsDataLoading = false;
    //     if (payload?.message)
    //       toast.error(() => CustomErrorToast(payload?.message));
    //   });

    //resource flavors
    // builder
    //   .addCase(getAiResourceFlavorsAsync.pending, (state, action) => {
    //     if (!action.meta.arg.withoutLoading) state.flavorsLoading = true;
    //   })
    //   .addCase(getAiResourceFlavorsAsync.fulfilled, (state, action) => {
    //     const { flavors } = action.payload;
    //     state.flavors = flavors;
    //     state.flavorsLoading = false;
    //   })
    //   .addCase(getAiResourceFlavorsAsync.rejected, (state, { payload }) => {
    //     state.flavorsLoading = false;
    //     if (payload?.message)
    //       toast.error(() => CustomErrorToast(payload?.message));
    //   });
  },
});

export const selectAiJobs = (state: RootState) => state.aiJobs.jobs;
export const selectAijobsLoading = (state: RootState) =>
  state.aiJobs.jobsLoading;

export const selectAiJobsActionLoading = (state: RootState) =>
  state.aiJobs.jobsActionLoading;

export const selectSelectedAiJobs = (state: RootState) =>
  state.aiJobs.selectedJob;

export const selectSelectedPresetImages = (state: RootState) =>
  state.aiJobs.presetImages;
export const selectSelectedPresetImagesLoading = (state: RootState) =>
  state.aiJobs.presetImagesLoading;

export const { handleSelectJob } = aiJobsSlice.actions;
export default aiJobsSlice.reducer;
